<form
	[formGroup]="citiworksForm"
	class="flex flex-col md:flex-row gap-2 md:gap-4 w-full h-full"
>
	<!-- Left Panel - Activities -->
	<div
		class="bg-white dark:bg-gray-700 rounded-lg shadow p-4 w-full md:w-1/2 flex-grow"
	>
		<h2 class="font-avertaBold text-gray-800 dark:text-gray-200 mb-4">
			Cityworks Activities
		</h2>

		<!-- Loading state -->
		@if (activitiesLoading) {
			<div class="py-2 text-sm text-gray-500 dark:text-gray-400">
				Loading activities...
			</div>
		}

		<!-- Activities list -->
		@if (!activitiesLoading && activities.length > 0) {
			<div
				class="space-y-2 min-h-60 h-[calc(100vh-355px)] scrollbar-thin scrollbar-thumb-[#000733]/40 dark:scrollbar-thumb-[#ffffff]/20 pr-1 scrollbar-track-transparent overflow-y-scroll"
				formArrayName="activities"
			>
				@for (
					activity of activitiesArray.controls;
					track i;
					let i = $index
				) {
					<div class="flex items-center">
						<div class="relative flex items-center">
							<input
								type="checkbox"
								[id]="'activity-' + activities[i].Activity"
								[formControlName]="i"
								(change)="onActivityToggle(i)"
								class="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
							/>
						</div>
						<label
							[for]="'activity-' + activities[i].Activity"
							class="ml-2 text-sm text-gray-700 dark:text-gray-300"
						>
							{{ activities[i].Activity }}
							{{ activities[i].Description }}
						</label>
					</div>
				}
			</div>
		}

		<!-- Empty state -->
		@if (!activitiesLoading && activities.length === 0) {
			<div class="py-2 text-sm text-gray-500 dark:text-gray-400">
				No activities available
			</div>
		}
	</div>

	<!-- Right Panel - Work Order Numbers -->
	<div
		class="bg-white dark:bg-gray-700 rounded-lg shadow p-4 w-full md:w-1/2 flex-grow"
	>
		<h2 class="font-avertaBold text-gray-800 dark:text-gray-200 mb-4">
			Cityworks IDs
		</h2>

		<!-- Top Button + Input Row -->
		<div class="flex items-center justify-between mb-2 gap-2">
			<div class="flex items-center gap-2">
				<button
					type="button"
					(click)="addWorkOrder()"
					class="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white rounded-full flex items-center justify-center border-2 border-slate-600 dark:border-slate-900"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						stroke-width="2.5"
						stroke="currentColor"
						class="size-6"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							d="M12 4.5v15m7.5-7.5h-15"
						/>
					</svg>
				</button>

				<div>
					<input
						type="text"
						formControlName="newWorkOrder"
						class="bg-white w-64 duration-200 border border-[#E2E2E2] py-2 px-4 rounded-lg dark:bg-gray-700 dark:border-gray-500 dark:text-white placeholder-gray-500 dark:placeholder-gray-500"
						placeholder="Enter Cityworks ID"
						onlyNumber
						[class.border-red-500]="
							(citiworksForm.controls['newWorkOrder'].invalid &&
								(citiworksForm.controls['newWorkOrder'].dirty ||
									citiworksForm.controls['newWorkOrder']
										.touched) &&
								citiworksFormSubmitted) ||
							duplicateError
						"
						[class.dark:border-red-500]="
							(citiworksForm.controls['newWorkOrder'].invalid &&
								(citiworksForm.controls['newWorkOrder'].dirty ||
									citiworksForm.controls['newWorkOrder']
										.touched) &&
								citiworksFormSubmitted) ||
							duplicateError
						"
						maxlength="6"
						autocomplete="off"
						size="6"
					/>

					@if (
						citiworksForm.controls['newWorkOrder'].invalid &&
						(citiworksForm.controls['newWorkOrder'].dirty ||
							citiworksForm.controls['newWorkOrder'].touched) &&
						citiworksFormSubmitted
					) {
						<div
							class="relative left-0 mt-1 text-xs text-red-600 dark:text-red-400"
						>
							@if (
								citiworksForm.controls['newWorkOrder'].errors?.[
									'required'
								]
							) {
								<div>Cityworks ID is required</div>
							}
							@if (
								citiworksForm.controls['newWorkOrder'].errors?.[
									'pattern'
								]
							) {
								<div>
									Cityworks ID can't be more than 6 digits
								</div>
							}
						</div>
					}
				</div>
			</div>

			<!-- Search Box -->
			<div class="relative">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					stroke-width="2"
					stroke="currentColor"
					class="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
					/>
				</svg>
				<input
					type="text"
					formControlName="searchWorkOrder"
					class="bg-white w-64 duration-200 border border-[#E2E2E2] py-2 pl-10 pr-4 rounded-lg dark:bg-gray-700 dark:border-gray-500 dark:text-white placeholder-gray-500 dark:placeholder-gray-500"
					placeholder="Search Cityworks ID..."
					onlyNumber
					autocomplete="off"
				/>
			</div>
		</div>

		<!-- Work Orders List -->
		<div
			class="space-y-2 min-h-60 h-[calc(100vh-355px)] scrollbar-thin scrollbar-thumb-[#000733]/40 dark:scrollbar-thumb-[#ffffff]/20 pr-1 scrollbar-track-transparent overflow-y-scroll"
		>
			@for (
				order of filteredWorkOrders;
				track order.CityworksWorkOrderID
			) {
				<div class="flex items-center gap-3 py-0.5 ps-12">
					<span
						class="font-avertaRegular text-sm text-gray-700 dark:text-gray-300"
					>
						{{ order.CityworksWOID }}
					</span>
					<span
						class="text-red-600 dark:text-red-500 text-sm font-bold cursor-pointer"
						(click)="removeWorkOrder(order)"
						(keydown.enter)="removeWorkOrder(order)"
						(keydown.space)="removeWorkOrder(order)"
					>
						-
					</span>
				</div>
			}

			<!-- Loading state for work orders -->
			@if (workOrdersLoading) {
				<div class="py-2 text-sm text-gray-500 dark:text-gray-400">
					Loading Cityworks IDs...
				</div>
			}

			<!-- Empty state for work orders -->
			@if (!workOrdersLoading && workOrders.length === 0) {
				<div
					class="py-2 text-sm text-gray-500 dark:text-gray-400 ps-12"
				>
					No Cityworks IDs available
				</div>
			}

			<!-- No search results -->
			@if (
				!workOrdersLoading &&
				workOrders.length > 0 &&
				filteredWorkOrders.length === 0
			) {
				<div
					class="py-2 text-sm text-gray-500 dark:text-gray-400 ps-12"
				>
					No Cityworks IDs found matching your search
				</div>
			}
		</div>
	</div>
</form>
